import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Customer, Individual } from '../../../services/schemas/customerServiceSchemas';
import { ContactDetailCreateContactType } from '../../../services/schemas/customerServiceSchemas/contactDetailCreateContactType';
import { useSearchCustomers, useGetCustomer, getCustomerById } from '../../../services/customerService';
import type { SearchCustomersParams } from '../../../services/schemas/customerServiceSchemas/searchCustomersParams';
import type { GetCustomerParams } from '../../../services/schemas/customerServiceSchemas/getCustomerParams';
import { INDIVIDUAL_ID_TYPES, IndividualIdType, usesCustomerIdentity, validateIndividualNumber } from '../../../utils/validation/individualIdentification';
import { toast } from 'react-toastify';

interface CustomerSearchStepProps {
  onSelect: (customer: Customer) => void;
  onSkip: () => void;
}

const NewCustomerSearchStep: React.FC<CustomerSearchStepProps> = ({
  onSelect,
  onSkip
}) => {
  const { watch } = useFormContext();
  const customerType = watch('customerType');

  const [searchType, setSearchType] = useState<'registrationNumber' | 'customerName' | 'idNumber'>('registrationNumber');
  const [searchValue, setSearchValue] = useState('');
  const [idType, setIdType] = useState<IndividualIdType>('SA_ID');
  const [searchParams, setSearchParams] = useState<SearchCustomersParams | undefined>();
  const [getCustomerParams, setGetCustomerParams] = useState<GetCustomerParams | undefined>();
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const normalizedStatus = (status?: string) => (status || '').toUpperCase();
  const isRestrictedStatus = (status?: string) => {
    const currentStatus = normalizedStatus(status);
    return currentStatus === 'INACTIVE' || currentStatus === 'SUSPENDED' || currentStatus === 'BLACKLISTED';
  };
  const showRestrictionToast = (status?: string) => {
    const currentStatus = normalizedStatus(status);
    const isBlacklisted = currentStatus === 'SUSPENDED' || currentStatus === 'BLACKLISTED';
    const isInactive = currentStatus === 'INACTIVE';
    const toastId = `customer-restriction-${currentStatus || 'unknown'}`;
    toast(isInactive ? 'Customer is inactive' : 'Customer is blacklisted', {
      toastId,
      type: isBlacklisted ? 'warning' : 'error',
      icon: isInactive
        ? <span role="img" aria-label="Inactive">⏸️</span>
        : isBlacklisted
        ? <span role="img" aria-label="Blacklisted">⛔</span>
        : <span role="img" aria-label="Suspended">⏸️</span>,
      autoClose: 4500,
      closeOnClick: true
    });
  };
  const guardRestrictedCustomer = (customer?: Customer) => {
    if (customer && isRestrictedStatus(customer.status)) {
      showRestrictionToast(customer.status);
      return true;
    }
    return false;
  };
  const renderStatusBadge = (status?: string) => {
    const currentStatus = normalizedStatus(status);
    if (!currentStatus) {
      return <span className="badge bg-light text-muted">Unknown</span>;
    }
    if (currentStatus === 'ACTIVE') {
      return (
        <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2 rounded-pill">
          <i className="bi bi-check-circle me-1"></i>
          Active
        </span>
      );
    }
    if (currentStatus === 'INACTIVE') {
      return (
        <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-3 py-2 rounded-pill">
          <i className="bi bi-pause-circle me-1"></i>
          Inactive
        </span>
      );
    }
    if (currentStatus === 'SUSPENDED' || currentStatus === 'BLACKLISTED') {
      return (
        <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2 rounded-pill">
          <i className="bi bi-shield-exclamation me-1"></i>
          {currentStatus === 'SUSPENDED' ? 'Suspended' : 'Blacklisted'}
        </span>
      );
    }
    return (
      <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-3 py-2 rounded-pill">
        <i className="bi bi-question-circle me-1"></i>
        {status}
      </span>
    );
  };
  const trimmedSearchValue = searchValue.trim();
  // Customer ID: a historical client is found by their customer number (e.g. DES001), which is their stored id number
  const idSearchValidationResult = searchType === 'idNumber'
    ? (usesCustomerIdentity(idType)
        ? (/^[A-Za-z]{3}\d{3,5}$/.test(trimmedSearchValue) || 'Enter a customer number such as DES001')
        : validateIndividualNumber(trimmedSearchValue,idType))
    : true;
  const idSearchError = searchType === 'idNumber' && trimmedSearchValue.length > 0 && idSearchValidationResult !== true
    ? idSearchValidationResult
    : '';

  // Corporate customer search query
  const { data: searchData, isLoading: isSearchLoading, error: searchError, refetch: refetchSearch } = useSearchCustomers(
    searchParams,
    {
      query: {
        enabled: false, // Manual trigger
      }
    }
  ) as { data: any; isLoading: boolean; error: any; refetch: () => void };

  // Individual customer search query
  const { data: customerData, isLoading: isCustomerLoading, error: customerError, refetch: refetchCustomer } = useGetCustomer(
    getCustomerParams,
    {
      query: {
        enabled: false, // Manual trigger
      }
    }
  ) as { data: Customer | undefined; isLoading: boolean; error: any; refetch: () => void };

  // Combine loading states
  const isLoading = isSearchLoading || isCustomerLoading;
  const error = searchError || customerError;

  // Handle search results
  useEffect(() => {
    if (searchData && !isSearchLoading) {
      const corporateRows = (searchData?.content || []).filter((c: Customer) => c.type === 'CORPORATE');
      console.groupCollapsed('[LoanApp][CustomerSearch] searchCustomers response');
      console.log('params:', searchParams);
      console.log('total results:', searchData?.content?.length || 0);
      console.log(
        'corporate representativeOccupation snapshot:',
        corporateRows.map((c: any) => ({
          id: c.id,
          customerNumber: c.customerNumber,
          representativeOccupation: c?.info?.representativeOccupation,
          firstDirectorDesignation: c?.info?.directors?.[0]?.designation
        }))
      );
      console.log('raw payload:', searchData);
      console.groupEnd();

      setSearchResults(searchData?.content || []);
      setHasSearched(true);
      setIsSearching(false);
    }
  }, [searchData, isSearchLoading, searchParams]);

  useEffect(() => {
    const person = customerData?.info?.type === 'INDIVIDUAL' ? customerData.info as Individual : undefined;
    const matchesIdentification = person && getCustomerParams && getCustomerParams.idNumber === person.idNumber
      && (person.idType || 'SA_ID') === (getCustomerParams.idType || 'SA_ID')
      && (person.idType || 'SA_ID') === idType;
    if (customerData && !isCustomerLoading && matchesIdentification) {
      console.groupCollapsed('[LoanApp][CustomerSearch] getCustomer response');
      console.log('params:', getCustomerParams);
      console.log('type:', customerData.type);
      console.log('info.type:', (customerData.info as any)?.type);
      console.log('representativeOccupation:', (customerData.info as any)?.representativeOccupation);
      console.log('firstDirectorDesignation:', (customerData.info as any)?.directors?.[0]?.designation);
      console.log('raw payload:', customerData);
      console.groupEnd();

      setSearchResults(customerData ? [customerData] : []);
      setHasSearched(true);
      setIsSearching(false);
    }
  }, [customerData, isCustomerLoading, getCustomerParams, idType]);

  useEffect(() => {
    if ((searchError || customerError) && !isLoading) {
      setSearchResults([]);
      setHasSearched(true);
      setIsSearching(false);
    }
  }, [searchError, customerError, isLoading]);

  const handleSearch = () => {
    if (!trimmedSearchValue) return;
    if (searchType === 'idNumber' && idSearchValidationResult !== true) return;

    setIsSearching(true);
    setHasSearched(false);
    setSearchResults([]);
    setSearchParams(undefined);
    setGetCustomerParams(undefined);

    if (customerType === 'individual' && searchType === 'idNumber') {
      // Search individual by ID number using getCustomer
      const params: GetCustomerParams = {
        idNumber: idType === 'SA_ID' ? trimmedSearchValue : trimmedSearchValue.toUpperCase(),
        ...(idType === 'PASSPORT' && {idType})
      };
      setGetCustomerParams(params);
      setTimeout(() => {
        refetchCustomer();
      }, 100);
    } else {
      // Search corporate customers using searchCustomers
      const params: SearchCustomersParams = {};

      if (searchType === 'registrationNumber') {
        params.registrationNumber = trimmedSearchValue;
      } else {
        params.registeredName = trimmedSearchValue;
      }

      setSearchParams(params);
      setTimeout(() => {
        refetchSearch();
      }, 100);
    }
  };

  const handleClear = () => {
    setSearchValue('');
    setSearchResults([]);
    setHasSearched(false);
    setSearchParams(undefined);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelect = async (customer: Customer) => {
    if (guardRestrictedCustomer(customer)) return;

    // Corporate list search can return partial info. Fetch full customer record before prefill.
    if (customer.type === 'CORPORATE' && customer.id) {
      try {
        setSelectedCustomerId(customer.id);
        const fullCustomer = await getCustomerById(customer.id);
        console.groupCollapsed('[LoanApp][CustomerSearch] getCustomerById response');
        console.log('customerId:', customer.id);
        console.log('representativeOccupation:', (fullCustomer?.info as any)?.representativeOccupation);
        console.log('firstDirectorDesignation:', (fullCustomer?.info as any)?.directors?.[0]?.designation);
        console.log('raw payload:', fullCustomer);
        console.groupEnd();
        if (guardRestrictedCustomer(fullCustomer)) {
          return;
        }
        onSelect(fullCustomer || customer);
        return;
      } catch (error) {
        console.error('Failed to fetch full customer details, falling back to search result:', error);
      } finally {
        setSelectedCustomerId(null);
      }
    }

    onSelect(customer);
  };

  // Reset search when customer type changes
  useEffect(() => {
    handleClear();
    // Set default search type based on customer type
    if (customerType === 'company') {
      setSearchType('registrationNumber');
    } else {
      setSearchType('idNumber');
    }
  }, [customerType]);

  return (
    <div className="customer-search-step">
      <div className="mb-4">
        <h4 className="fw-bold">Customer Search</h4>
        <p className="text-muted">
          Search for an existing {customerType === 'company' ? 'corporate' : 'individual'} customer to pre-populate the loan application, or skip to create a new application.
        </p>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <h5 className="card-title mb-3">Search Criteria</h5>

          {/* Search Type Selection */}
          {customerType === 'company' && (
            <div className="row mb-4">
              <div className="col-12">
                <label className="form-label fw-medium mb-3">How would you like to search?</label>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div 
                      className={`card h-100 cursor-pointer border-2 ${searchType === 'registrationNumber' ? 'border-primary bg-white shadow' : 'border-light'}`}
                      onClick={() => setSearchType('registrationNumber')}
                      style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      <div className="card-body text-center p-4">
                        <div className={`mb-3 ${searchType === 'registrationNumber' ? 'text-primary' : 'text-muted'}`}>
                          <i className="bi bi-hash" style={{ fontSize: '2rem' }}></i>
                        </div>
                        <h6 className={`mb-2 fw-bold ${searchType === 'registrationNumber' ? 'text-dark' : ''}`}>
                          Registration Number
                        </h6>
                        <small className={searchType === 'registrationNumber' ? 'text-dark' : 'text-muted'}>
                          Search using company registration number
                        </small>
                        <div className="mt-3">
                          <div className={`form-check d-flex justify-content-center ${searchType === 'registrationNumber' ? 'text-primary' : ''}`}>
                            <input
                              className="form-check-input"
                              type="radio"
                              name="searchType"
                              checked={searchType === 'registrationNumber'}
                              onChange={() => setSearchType('registrationNumber')}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div 
                      className={`card h-100 cursor-pointer border-2 ${searchType === 'customerName' ? 'border-primary bg-white shadow' : 'border-light'}`}
                      onClick={() => setSearchType('customerName')}
                      style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      <div className="card-body text-center p-4">
                        <div className={`mb-3 ${searchType === 'customerName' ? 'text-primary' : 'text-muted'}`}>
                          <i className="bi bi-building" style={{ fontSize: '2rem' }}></i>
                        </div>
                        <h6 className={`mb-2 fw-bold ${searchType === 'customerName' ? 'text-dark' : ''}`}>
                          Company Name
                        </h6>
                        <small className={searchType === 'customerName' ? 'text-dark' : 'text-muted'}>
                          Search using registered company name
                        </small>
                        <div className="mt-3">
                          <div className={`form-check d-flex justify-content-center ${searchType === 'customerName' ? 'text-primary' : ''}`}>
                            <input
                              className="form-check-input"
                              type="radio"
                              name="searchType"
                              checked={searchType === 'customerName'}
                              onChange={() => setSearchType('customerName')}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {customerType === 'individual' && <div className="mb-3">
            <label htmlFor="search-id-type" className="form-label">Identification Type</label>
            <select id="search-id-type" className="form-select" value={idType} onChange={event => {
              setIdType(event.target.value as IndividualIdType);setSearchValue('');setSearchResults([]);setHasSearched(false);
            }}>{INDIVIDUAL_ID_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}</select>
          </div>}
          {/* Search Input */}
          <div className="row mb-4">
            <div className="col-12">
              <label htmlFor="searchValue" className="form-label fw-medium">
                {searchType === 'registrationNumber'
                  ? 'Company Registration Number'
                  : searchType === 'customerName'
                    ? 'Company Name'
                    : usesCustomerIdentity(idType) ? 'Customer ID' : idType === 'PASSPORT' ? 'Passport Number' : 'ID Number'
                }
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <i className={`bi ${searchType === 'idNumber' ? 'bi-person' : 'bi-building'}`}></i>
                </span>
                <input
                  type="text"
                  className={`form-control form-control-lg ${idSearchError ? 'is-invalid' : ''}`}
                  id="searchValue"
                  placeholder={
                    searchType === 'registrationNumber'
                      ? 'Enter company registration number'
                      : searchType === 'customerName'
                        ? 'Enter company name'
                        : usesCustomerIdentity(idType) ? 'Enter customer number, e.g. DES001'
                        : idType === 'PASSPORT' ? 'Enter passport number' : 'Enter 13-digit ID number'
                  }
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
              </div>
              {idSearchError && (
                <div className="invalid-feedback d-block">{idSearchError}</div>
              )}
              <div className="form-text">
                {searchType === 'registrationNumber'
                  ? 'Search for a company by their registration number'
                  : searchType === 'customerName'
                    ? 'Search for a company by their registered name'
                    : `Search for an individual customer by their ${usesCustomerIdentity(idType) ? 'customer' : idType === 'PASSPORT' ? 'passport' : 'ID'} number`
                }
              </div>
            </div>
          </div>

          {/* Search Actions */}
          <div className="d-grid gap-2 d-md-flex justify-content-md-between">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onSkip}
            >
              <i className="bi bi-skip-forward me-2"></i>
              Skip Search
            </button>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleClear}
                disabled={!searchValue && !hasSearched}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Clear
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={isLoading || !trimmedSearchValue ||
                  (searchType === 'customerName' && trimmedSearchValue.length < 2) ||
                  (searchType === 'idNumber' && idSearchValidationResult !== true)
                }
                onClick={handleSearch}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Searching...
                  </>
                ) : (
                  <>
                    <i className="bi bi-search me-2"></i>
                    Search
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search Results */}
      {hasSearched && !isLoading && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-4">
            <h5 className="card-title mb-3">
              <i className="bi bi-list-ul me-2"></i>
              Search Results
            </h5>

            {searchResults.length > 0 ? (
              <>
                <p className="text-muted mb-3">
                  Found {searchResults.length} customer{searchResults.length !== 1 ? 's' : ''} matching your search criteria.
                </p>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Customer Number</th>
                        <th>Type</th>
                        <th>Name</th>
                        <th>Registration/ID</th>
                        <th>Status</th>
                        <th>Contact</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((customer, index) => (
                        <tr key={index} className={isRestrictedStatus(customer.status) ? 'table-warning align-middle' : 'align-middle'}>
                          <td>
                            <span className="badge bg-secondary">
                              {customer.customerNumber || 'N/A'}
                            </span>
                          </td>
                          <td>
                            {customer.type === 'CORPORATE' ? (
                              <span className="badge bg-info">
                                <i className="bi bi-building me-1"></i>Corporate
                              </span>
                            ) : (
                              <span className="badge bg-success">
                                <i className="bi bi-person me-1"></i>Individual
                              </span>
                            )}
                          </td>
                          <td>
                            <div>
                              <strong>
                                {customer.type === 'CORPORATE'
                                  ? ((customer.info as any)?.registeredName || 'N/A')
                                  : `${(customer.info as any)?.firstName || ''} ${(customer.info as any)?.lastName || ''}`.trim() || 'N/A'
                                }
                              </strong>
                            </div>
                          </td>
                          <td>
                            <small className="text-muted">
                              {customer.type === 'CORPORATE'
                                ? ((customer.info as any)?.registrationNumber || 'N/A')
                                : ((customer.info as any)?.idNumber || 'N/A')
                              }
                            </small>
                          </td>
                          <td>
                            {renderStatusBadge(customer.status)}
                          </td>
                          <td>
                            {customer.contacts && customer.contacts.length > 0 ? (
                              <div>
                                {customer.contacts
                                  .filter(contact => contact.primary)
                                  .slice(0, 1)
                                  .map((contact, idx) => (
                                    <small key={idx} className="text-muted">
                                      {contact.contactType === ContactDetailCreateContactType.EMAIL && <i className="bi bi-envelope me-1"></i>}
                                      {contact.contactType === ContactDetailCreateContactType.MOBILE && <i className="bi bi-phone me-1"></i>}
                                      {contact.contactValue}
                                    </small>
                                  ))
                                }
                                {customer.contacts.filter(contact => contact.primary).length === 0 && (
                                  <small className="text-muted">
                                    {customer.contacts[0]?.contactValue || 'N/A'}
                                  </small>
                                )}
                              </div>
                            ) : (
                              <small className="text-muted">No contact info</small>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`btn btn-sm ${isRestrictedStatus(customer.status) ? 'btn-outline-danger border-2' : 'btn-primary'}`}
                              disabled={isLoading || (selectedCustomerId !== null && selectedCustomerId !== customer.id)}
                              onClick={() => handleSelect(customer)}
                              title={isRestrictedStatus(customer.status) ? 'This customer cannot be used for a loan application' : 'Select customer'}
                            >
                              {selectedCustomerId === customer.id ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                  Loading...
                                </>
                              ) : (
                                <>
                                  <i className={`me-1 ${isRestrictedStatus(customer.status) ? 'bi bi-shield-lock' : 'bi bi-check-circle'}`}></i>
                                  {isRestrictedStatus(customer.status) ? 'Attempt Select' : 'Select'}
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="mb-3">
                  <i className="bi bi-search text-muted" style={{ fontSize: '2rem' }}></i>
                </div>
                <h6 className="text-muted mb-2">No customers found</h6>
                <p className="text-muted mb-3">
                  No customers match your search criteria. You can try different search terms or skip to create a new application.
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onSkip}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Create New Application
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>
            Error searching for customers. Please try again or skip to create a new application.
          </div>
        </div>
      )}
    </div>
  );
};

export default NewCustomerSearchStep;
